// src/components/charts/ChartCard.jsx
//
// The card every Dashboard chart sits in: title / subtitle / loading / empty
// states, plus a `reliability` badge. Charts fall in two buckets — population-true
// (the /runs/stats endpoint, exact over the period) and sample-based (the /runs
// rows endpoint, capped at limit=200) — and the badge says which.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_SANS, SEMANTIC } from '../../constants/theme.js';

const FONT = FONT_SANS;
const COLOR = { text: COLORS.ink, muted: COLORS.textSecondary, border: COLORS.border };

// Small pill indicating whether a chart is exact over the whole period
// (population-true /runs/stats) or a recent capped sample (/runs?limit=200).
// Kept here so every card renders the badge identically.
function ReliabilityBadge({ reliability }) {
  const { t } = useTranslation('dashboard');
  if (!reliability) return null;
  const isExact = reliability.kind === 'exact';
  const label = isExact ? t('reliability.all') : t('reliability.sample', { count: reliability.n });
  const bg = isExact ? SEMANTIC.verde.bg : SEMANTIC.attenzione.bg;
  const fg = isExact ? '#0F8F6B' : '#B26B00';
  return (
    <span
      title={isExact ? t('reliability.allTitle') : t('reliability.sampleTitle')}
      style={{
        fontSize: 10, fontWeight: 600, textTransform: 'none', letterSpacing: 0,
        padding: '2px 8px', borderRadius: 999, background: bg, color: fg,
        whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
}

export default function ChartCard({ title, subtitle, loading, empty, reliability, children }) {
  const { t } = useTranslation(['dashboard', 'common']);
  return (
    <div style={{
      padding: '16px 18px', borderRadius: 8,
      border: `1px solid ${COLOR.border}`, background: COLORS.surface,
      marginBottom: 16, fontFamily: FONT,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, marginBottom: 4,
      }}>
        <div style={{ fontSize: 11, color: COLOR.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {title}
        </div>
        <ReliabilityBadge reliability={reliability} />
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: COLOR.muted, marginBottom: 12 }}>{subtitle}</div>
      )}
      {loading && <div style={{ fontSize: 13, color: COLOR.muted, padding: '24px 0' }}>{t('common:loading')}</div>}
      {!loading && empty && (
        <div style={{ fontSize: 13, color: COLOR.muted, padding: '24px 0' }}>{t('card.empty')}</div>
      )}
      {!loading && !empty && children}
    </div>
  );
}
